/**
 * Data layer for `amazon-advertising-integration`. Thin typed fetch wrappers over /api/v1/amazon-advertising-integration.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface AmazonAdvertisingIntegrationRow {
	id: number | string;
	tenant_id: string;
	event_id?: string;
	profile_id?: string;
	advertiser_id?: string | null;
	conversion_type?: string;
	conversion_value?: number | null;
	conversion_currency?: string | null;
	marketplace?: string | null;
	status?: string;
	created_at: number;
	[key: string]: unknown;
}

export interface ListAmazonAdvertisingIntegrationResponse {
	items: AmazonAdvertisingIntegrationRow[];
}

export interface CreateAmazonAdvertisingIntegrationBody {
	event_id: string;
	profile_id: string;
	conversion_type: string;
	advertiser_id?: string;
	conversion_value?: number;
	conversion_currency?: string;
	marketplace?: string;
	skan_payload?: string | Record<string, unknown>;
	[key: string]: unknown;
}

const BASE = "/api/v1/amazon-advertising-integration";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListAmazonAdvertisingIntegrationResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAmazonAdvertisingIntegrationResponse>(res);
}

export async function create(body: CreateAmazonAdvertisingIntegrationBody): Promise<AmazonAdvertisingIntegrationRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<AmazonAdvertisingIntegrationRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const amazonAdvertisingIntegrationQueryKeys = {
	all: ["amazon-advertising-integration"] as const,
	list: () => [...amazonAdvertisingIntegrationQueryKeys.all] as const,
	detail: (id: string) => [...amazonAdvertisingIntegrationQueryKeys.all, id] as const,
};
